/**
 * StatisticsDashboard.jsx - Dashboard des statistiques sanitaires
 * Version Ultra Premium - Design Glassmorphique + Animations
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';
import AnalyticsService from '../../services/AnalyticsService';
import TrackingService from '../../services/TrackingService';
import {
  FiDownload,
  FiUsers, FiAlertTriangle,
  FiActivity, FiClock, FiFileText,
  FiZap, FiShield, FiHeart, FiCalendar, FiTarget
} from 'react-icons/fi';
import { FaPills } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import './StatisticsDashboard.css';

// ─── Couleurs premium ──────────────────────────────────────────────────────
const COLORS = {
  primary: '#4F46E5',
  primaryLight: '#818CF8',
  secondary: '#10B981',
  accent: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  purple: '#8B5CF6',
  pink: '#EC4899',
  teal: '#14B8A6',
  indigo: '#6366F1',
  rose: '#F43F5E',
  dark: '#1F2937',
  gray: '#6B7280',
  light: '#F9FAFB',
  white: '#FFFFFF',
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.accent, COLORS.danger, COLORS.purple, COLORS.pink, COLORS.teal, COLORS.indigo];

const TOOLTIP_STYLE = { borderRadius: 16, border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' };

// ─── Utilitaires ────────────────────────────────────────────────────────────
function formatAnalysisType(type) {
  const types = {
    DRUG_INTERACTION: 'Interactions 💊',
    CONTRAINDICATION: 'Contre-indications ⚠️',
    THERAPEUTIC_EQUIVALENCE: 'Équivalences 🔄',
    DOSAGE_ANALYSIS: 'Dosages 📏',
  };
  return types[type] || type;
}

function formatDate(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch (e) {
    return dateStr;
  }
}

function getResponseArray(response, keys = []) {
  const payloads = [
    response,
    response && response.data,
    response && response.result,
    response && response.data && response.data.data,
  ];
  keys.forEach(key => {
    payloads.push(response && response[key]);
    payloads.push(response && response.data && response.data[key]);
    payloads.push(response && response.data && response.data.data && response.data.data[key]);
  });

  return payloads.find(Array.isArray) || [];
}

function getGlobalStatsPayload(response) {
  const data = response && response.data;
  return (data && (data.globalStats || data.statistics || data.stats)) ||
    (data && data.data && (data.data.globalStats || data.data.statistics || data.data.stats)) ||
    (data && data.data) ||
    (response && (response.globalStats || response.statistics || response.stats)) ||
    data || response || {};
}

function normalizeAnalysisTypes(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).map(([type, count]) => ({
    _id: type,
    count: typeof count === 'number'
      ? count
      : count && (count.count || count.total || count.totalAnalyses || count.analysisCount || count.totalCount),
  }));
}

function findAnalysisTypeData(value, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 4) return undefined;
  const typeKeys = ['byType', 'analysisTypes', 'byAnalysisType', 'analysisTypesStats', 'typeStats', 'analysisTypeStats'];
  for (const key of typeKeys) {
    if (value[key] !== undefined) return value[key];
  }

  for (const nestedValue of Object.values(value)) {
    const found = findAnalysisTypeData(nestedValue, depth + 1);
    if (found !== undefined) return found;
  }
  return undefined;
}

function normalizeTypeName(value) {
  if (value && typeof value === 'object') {
    return normalizeTypeName(value.analysisType || value.analysis_type || value.type || value.name || value._id || '');
  }
  return String(value || '').toUpperCase().replace(/[^A-Z]/g, '');
}

function getAnalysisTypeCount(analysisTypes, type) {
  const aliases = type === 'CONTRAINDICATION'
    ? ['CONTRAINDICATION', 'CONTRAINDICATIONS', 'CONTREINDICATION', 'CONTREINDICATIONS']
    : [type.replace(/[^A-Z]/g, '')];
  return analysisTypes.reduce((total, item) => {
    const itemType = normalizeTypeName(item._id || item.type || item.analysisType || item.analysis_type || item.name);
    if (!aliases.some(alias => itemType === alias || itemType.includes(alias))) return total;
    return total + (Number(item.count || item.total || item.totalAnalyses || item.analysisCount || item.totalCount) || 0);
  }, 0);
}

function getContraindicationCount(analysisTypes, stats, globalData) {
  const byTypeCount = getAnalysisTypeCount(analysisTypes, 'CONTRAINDICATION');
  const explicitCount = [
    stats.contraindicationCount,
    stats.contraindicationsCount,
    stats.totalContraindications,
    stats.contraindicationAnalyses,
    stats.contraindicationAnalysisCount,
    globalData.contraindicationCount,
    globalData.contraindicationsCount,
    globalData.totalContraindications,
    globalData.contraindicationAnalyses,
    globalData.contraindicationAnalysisCount,
  ].map(Number).find(count => Number.isFinite(count) && count > 0) || 0;

  const nestedCount = findContraindicationCount(globalData);
  return Math.max(byTypeCount, explicitCount, nestedCount);
}

function isContraindicationType(value) {
  const type = normalizeTypeName(value);
  return ['CONTRAINDICATION', 'CONTRAINDICATIONS', 'CONTREINDICATION', 'CONTREINDICATIONS']
    .some(alias => type === alias || type.includes(alias));
}

function getCountValue(value) {
  if (typeof value === 'number' || typeof value === 'string') {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
  }
  if (!value || typeof value !== 'object') return 0;
  return Number(
    value.count || value.total || value.totalAnalyses || value.analysisCount ||
    value.totalCount || value.value
  ) || 0;
}

function findContraindicationCount(value, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 6) return 0;
  let maxCount = 0;

  Object.entries(value).forEach(([key, nestedValue]) => {
    const normalizedKey = key.toUpperCase().replace(/[^A-Z]/g, '');
    if (isContraindicationType(normalizedKey)) {
      maxCount = Math.max(maxCount, getCountValue(nestedValue));
    }

    if (nestedValue && typeof nestedValue === 'object') {
      const nestedType = nestedValue._id || nestedValue.type || nestedValue.analysisType ||
        nestedValue.analysis_type || nestedValue.name;
      if (isContraindicationType(nestedType)) {
        maxCount = Math.max(maxCount, getCountValue(nestedValue));
      }
    }
    if (nestedValue && typeof nestedValue === 'object') {
      maxCount = Math.max(maxCount, findContraindicationCount(nestedValue, depth + 1));
    }
  });

  return maxCount;
}

// ─── Composant Principal ────────────────────────────────────────────────────
const StatisticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [activeTab, setActiveTab] = useState('overview');

  // Chargement de toutes les données en une seule fois (évite la boucle infinie de l'ancienne version)
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [globalStats, trends, topMedications, userStats] = await Promise.all([
        AnalyticsService.getGlobalStatistics(dateRange.startDate, dateRange.endDate),
        AnalyticsService.getTemporalTrends(dateRange.startDate, dateRange.endDate, 'daily'),
        AnalyticsService.getTopMedications(dateRange.startDate, dateRange.endDate, 10),
        AnalyticsService.getStatisticsByUser(dateRange.startDate, dateRange.endDate, 1, 10),
      ]);

      if (globalStats?.success === false) {
        throw new Error(globalStats.message || 'Impossible de charger les statistiques globales');
      }
      const failedResponse = [trends, topMedications, userStats].find(response => response?.success === false);
      if (failedResponse) {
        throw new Error(failedResponse.message || failedResponse.error || 'Impossible de charger toutes les données statistiques');
      }

      setDashboardData({
        globalStats: getGlobalStatsPayload(globalStats),
        trends: getResponseArray(trends, ['trends', 'results']),
        topMedications: getResponseArray(topMedications, ['topMedications', 'medications', 'results']),
        userStats: getResponseArray(userStats, ['userStats', 'users', 'results']),
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    const refreshStatistics = async () => {
      try {
        await TrackingService.syncOfflineAnalyses();
      } catch (syncError) {
        console.error('La synchronisation des analyses en attente a échoué.', syncError);
      }
      await fetchDashboardData();
    };
    refreshStatistics();
  }, [fetchDashboardData]);

  // Doit être déclaré AVANT handleExport qui l'utilise
  const derivedData = useMemo(() => {
    if (!dashboardData) return null;
    const globalData = dashboardData.globalStats || {};
    const totalStats = globalData.totalStats || globalData.summary || globalData.stats || [];
    const stats = Array.isArray(totalStats) ? (totalStats[0] || {}) : totalStats;
    const analysisTypes = normalizeAnalysisTypes(findAnalysisTypeData(globalData));
    const trends = dashboardData.trends || [];
    const topUsers = dashboardData.userStats || [];
    const topMeds = dashboardData.topMedications || [];
    return {
      stats, analysisTypes, trends, topUsers, topMeds,
      typeChartData: analysisTypes.map(t => ({ name: formatAnalysisType(t._id), total: t.count || 0 })),
      trendChartData: trends.map(t => ({ date: formatDate(t._id), total: t.totalAnalyses || 0 })),
      userChartData: topUsers.slice(0, 5).map(u => ({ name: u._id || 'Inconnu', total: u.analysisCount || 0 })),
    };
  }, [dashboardData]);

  const handleExport = useCallback(() => {
    try {
      const exportData = {
        stats: derivedData?.stats || {},
        period: `${dateRange.startDate} au ${dateRange.endDate}`,
        generatedAt: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statistiques_sanitaires_${dateRange.startDate}_${dateRange.endDate}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Erreur lors de l'export du rapport");
    }
  }, [dateRange, derivedData]);

  if (loading) return <PremiumLoadingScreen />;
  if (!derivedData) return <ErrorScreen error={error} onRetry={fetchDashboardData} />;

  const { stats, typeChartData, trendChartData, userChartData, topMeds, analysisTypes } = derivedData;
  const contraindicationAnalyses = getContraindicationCount(analysisTypes, stats, dashboardData.globalStats);

  const tabs = [
    { id: 'overview', label: "Vue d'ensemble", icon: FiActivity },
    { id: 'analysis', label: 'Analyses médicamenteuses', icon: FaPills },
    { id: 'users', label: 'Utilisateurs', icon: FiUsers },
    { id: 'impact', label: 'Impact sanitaire', icon: FiHeart },
  ];

  return (
      <div className="statistics-dashboard-premium">
        <div className="dashboard-container">
          {/* En-tête */}
          <header className="dashboard-header-premium">
            <div className="header-glow"></div>
            <div className="header-content">
              <div className="header-controls">
                <div className="control-group-premium">
                  <label><FiCalendar size={14} /> Période</label>
                  <div className="date-range-group">
                    <input type="date" className="date-input-premium" value={dateRange.startDate} onChange={e => setDateRange(d => ({ ...d, startDate: e.target.value }))} />
                    <span className="date-sep">→</span>
                    <input type="date" className="date-input-premium" value={dateRange.endDate} onChange={e => setDateRange(d => ({ ...d, endDate: e.target.value }))} />
                  </div>
                </div>
                <button className="btn-export-premium" onClick={handleExport}>
                  <FiDownload size={18} />
                  <span>Exporter rapport</span>
                </button>
              </div>
            </div>
          </header>

          {/* KPI Cards */}
          <div className="kpi-grid-premium">
            <KPICardPremium title="Analyses totales" value={stats.totalAnalyses || 0} icon={FiFileText} color="primary" subtitle="Analyses effectuées" />
            <KPICardPremium title="Analyses de contre-indication" value={contraindicationAnalyses} icon={FiShield} color="secondary" subtitle="Analyses réalisées" />
            <KPICardPremium title="Utilisateurs actifs" value={stats.uniqueUsers || 0} icon={FiUsers} color="purple" subtitle="Professionnels de santé" />
            <KPICardPremium title="Temps moyen" value={Math.round(stats.avgTimeSpent || 0)} suffix="s" icon={FiClock} color="pink" subtitle="par analyse" />
          </div>

          {/* Onglets */}
          <div className="tab-navigation-premium">
            {tabs.map(tab => (
                <button key={tab.id} className={`tab-btn-premium ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
            ))}
          </div>

          {/* Contenu */}
          <div className="tab-content-premium">
            {activeTab === 'overview' && <OverviewTab trendData={trendChartData} typeData={typeChartData} />}
            {activeTab === 'analysis' && <AnalysisTab typeData={typeChartData} topMeds={topMeds} />}
            {activeTab === 'users' && <UsersTab userData={userChartData} />}
            {activeTab === 'impact' && <ImpactTab analysisTypes={analysisTypes} stats={stats} />}
          </div>
        </div>
      </div>
  );
};

// ─── Sous-composants ─────────────────────────────────────────────────────────

const KPICardPremium = ({ title, value, suffix = '', icon: Icon, color, subtitle }) => {
  const [counted, setCounted] = useState(0);

  useEffect(() => {
    const target = Number(value) || 0;
    const decimals = Number.isInteger(target) ? 0 : 1;
    let start = null;
    let frame;
    const duration = 1500;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min(1, (timestamp - start) / duration);
      setCounted(+(progress * target).toFixed(decimals));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return (
      <div className={`kpi-card-premium ${color}`}>
        <div className="kpi-card-inner">
          <div className="kpi-icon-wrapper">
            <Icon size={28} />
          </div>
          <div className="kpi-info">
            <div className="kpi-label">{title}</div>
            <div className="kpi-value">{counted.toLocaleString('fr-FR')}{suffix}</div>
            {subtitle && <div className="kpi-subtitle">{subtitle}</div>}
          </div>
        </div>
      </div>
  );
};

const OverviewTab = ({ trendData, typeData }) => (
    <div className="chart-grid-premium">
      <div className="chart-card-premium">
        <div className="chart-header">
          <h3>📈 Évolution temporelle</h3>
          <p>Tendance des analyses sur la période</p>
        </div>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={trendData.length ? trendData : [{ date: 'N/A', total: 0 }]}>
            <defs>
              <linearGradient id="areaTotal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} /><stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Area type="monotone" dataKey="total" name="Total analyses" stroke={COLORS.primary} fill="url(#areaTotal)" strokeWidth={3} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-card-premium">
        <div className="chart-header">
          <h3>📊 Répartition par type</h3>
          <p>Distribution des analyses effectuées</p>
        </div>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={typeData.length ? typeData : [{ name: 'N/A', total: 0 }]} barGap={8}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="total" name="Total" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
);

const AnalysisTab = ({ typeData, topMeds }) => (
    <div className="chart-grid-premium">
      <div className="chart-card-premium">
        <div className="chart-header">
          <h3>🥧 Distribution circulaire</h3>
          <p>Proportion par type d'analyse</p>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
                data={typeData.length ? typeData : [{ name: 'N/A', total: 1 }]}
                cx="50%" cy="50%" innerRadius={80} outerRadius={130} paddingAngle={5}
                dataKey="total"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={{ stroke: COLORS.gray, strokeWidth: 1 }}
            >
              {(typeData.length ? typeData : [{ name: 'N/A' }]).map((_, i) => <Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-card-premium">
        <div className="chart-header">
          <h3>💊 Top médicaments analysés</h3>
          <p>Les substances les plus étudiées</p>
        </div>
        <div className="table-container-premium">
          <table className="data-table-premium">
            <thead>
            <tr><th>Médicament</th><th>Code ATC</th><th>Analyses</th><th>⚠️ Interactions</th><th>🚫 Contre-indications</th></tr>
            </thead>
            <tbody>
            {topMeds && topMeds.length ? topMeds.slice(0, 8).map((med, i) => (
                <tr key={i}>
                  <td><strong>{med.drugName || 'N/A'}</strong></td>
                  <td><span className="badge-premium">{med.atcCode || 'N/A'}</span></td>
                  <td>{med.analysisCount || 0}</td>
                  <td className="text-danger">{med.interactionsCaught || 0}</td>
                  <td className="text-warning">{med.contraindictionsCaught || 0}</td>
                </tr>
            )) : <tr><td colSpan="5" style={{ textAlign: 'center' }}>Aucune donnée disponible</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
);

const UsersTab = ({ userData }) => (
    <div className="chart-grid-premium">
      <div className="chart-card-premium full-width">
        <div className="chart-header">
          <h3>👥 Top 5 professionnels les plus actifs</h3>
          <p>Utilisateurs ayant généré le plus d'analyses</p>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={userData.length ? userData : [{ name: 'N/A', total: 0 }]} layout="vertical" barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis type="number" tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 13, fill: '#1F2937' }} width={150} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="total" name="Total analyses" fill={COLORS.primary} radius={[0, 10, 10, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
);

const ImpactTab = ({ analysisTypes, stats }) => (
    <>
      <div className="impact-grid-premium">
        <div className="impact-card-premium">
          <div className="impact-icon"><FiTarget size={32} /></div>
          <div className="impact-value">{stats.totalAnalyses?.toLocaleString('fr-FR') || 0}</div>
          <div className="impact-label">Analyses réalisées</div>
          <div className="impact-desc">Interactions, contre-indications et équivalences</div>
        </div>
        <div className="impact-card-premium">
          <div className="impact-icon"><FiAlertTriangle size={32} /></div>
          <div className="impact-value">{analysisTypes.find(t => t._id === 'DRUG_INTERACTION')?.count || 0}</div>
          <div className="impact-label">Interactions détectées</div>
          <div className="impact-desc">Interactions médicamenteuses identifiées</div>
        </div>
        <div className="impact-card-premium">
          <div className="impact-icon"><FiShield size={32} /></div>
          <div className="impact-value">{getContraindicationCount(analysisTypes, stats, {})}</div>
          <div className="impact-label">Contre-indications</div>
          <div className="impact-desc">Contre-indications consultées</div>
        </div>
        <div className="impact-card-premium">
          <div className="impact-icon"><FiZap size={32} /></div>
          <div className="impact-value">{analysisTypes.find(t => t._id === 'THERAPEUTIC_EQUIVALENCE')?.count || 0}</div>
          <div className="impact-label">Équivalences thérapeutiques</div>
          <div className="impact-desc">Substitutions de médicaments suggérées</div>
        </div>
      </div>
    </>
);

const PremiumLoadingScreen = () => (
    <div className="loading-premium">
      <div className="loading-spinner-premium"></div>
      <h3>Chargement des données sanitaires...</h3>
      <p>Analyse des indicateurs en cours</p>
    </div>
);

const ErrorScreen = ({ error, onRetry }) => (
    <div className="error-premium">
      <FiAlertTriangle size={48} />
      <h3>Erreur de chargement</h3>
      <p>{error || 'Impossible de charger les données'}</p>
      <button className="btn-retry-premium" onClick={onRetry}>Réessayer</button>
    </div>
);

export default StatisticsDashboard;